import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowClockwise, Warning } from "@phosphor-icons/react";

/**
 * App-wide error boundary. Catches render / lifecycle errors so a single bad
 * component (missing import, undefined prop, etc.) can't blank the whole app.
 * Shows a friendly "Something went wrong" screen with a reset action.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep it verbose in the console for devs; never crash silently.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen brand-gradient-soft flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-lg w-full card-elevated p-8 text-center">
          <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-2xl bg-rose-500/10 text-rose-600 mb-4">
            <Warning size={26} weight="fill" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            The app hit an unexpected error and this screen couldn't render.
            Try again, and if the issue persists reload the page.
          </p>
          {this.state.error?.message && (
            <div className="text-xs text-muted-foreground bg-muted rounded-md p-3 mb-6 text-left font-mono break-words">
              {String(this.state.error.message).slice(0, 300)}
            </div>
          )}
          <div className="flex items-center justify-center gap-3">
            <Button onClick={this.handleReset} variant="outline" data-testid="error-boundary-retry">
              <ArrowClockwise size={16} className="mr-2" weight="bold" /> Try again
            </Button>
            <Button onClick={this.handleReload} data-testid="error-boundary-reload">
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
